// src/services/settingsService.js
import tenantDataService from './tenantDataService';

const COLLECTION_NAME = 'settings';
const SETTINGS_DOC_ID = 'clinic_settings';

class SettingsService {
  // Get clinic settings
  async getSettings() {
    const settings = await tenantDataService.getById(COLLECTION_NAME, SETTINGS_DOC_ID);
    return settings || this.getDefaultSettings();
  }

  // Update clinic settings
  async updateSettings(settingsData) {
    try {
      // Try to update existing settings
      const existingSettings = await tenantDataService.getById(COLLECTION_NAME, SETTINGS_DOC_ID);
      if (existingSettings) {
        return await tenantDataService.update(COLLECTION_NAME, SETTINGS_DOC_ID, settingsData);
      } else {
        // Create new settings document with specific ID
        return await tenantDataService.create(COLLECTION_NAME, {
          id: SETTINGS_DOC_ID,
          ...settingsData
        });
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  }

  // Get default settings
  getDefaultSettings() {
    return {
      clinicName: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      treatmentTypes: [
        { name: 'Physiotherapy', pricingType: 'hourly', value: 150 },
        { name: 'Massage Therapy', pricingType: 'hourly', value: 120 },
        { name: 'Consultation', pricingType: 'flat', value: 80 }
      ],
      roleRates: {
        admin: 70,
        senior_clinician: 75,
        clinician: 65,
        junior_clinician: 55,
        staff: 45
      },
      vatPercentage: 20,
      bankDetails: {
        accountName: '',
        accountNumber: '',
        sortCode: ''
      },
      startingInvoiceNumber: 1,
      businessHours: {
        monday: { open: '09:00', close: '17:00', closed: false },
        tuesday: { open: '09:00', close: '17:00', closed: false },
        wednesday: { open: '09:00', close: '17:00', closed: false },
        thursday: { open: '09:00', close: '17:00', closed: false },
        friday: { open: '09:00', close: '17:00', closed: false },
        saturday: { open: '09:00', close: '13:00', closed: false },
        sunday: { open: '09:00', close: '17:00', closed: true }
      },
      appointmentDurations: [15, 30, 45, 60, 90, 120],
      defaultAppointmentDuration: 60,
      timeSlotInterval: 15,
      maxAdvanceBooking: 90, // days
      cancellationPolicy: '',
      paymentTerms: 'Payment due within 30 days',
      currency: 'GBP',
      currencySymbol: '£',
      dateFormat: 'DD/MM/YYYY',
      timeFormat: '24h'
    };
  }

  // Subscribe to settings changes
  subscribeToSettings(callback) {
    return tenantDataService.subscribe(COLLECTION_NAME, (allSettings) => {
      const settings = allSettings.find(s => s.id === SETTINGS_DOC_ID);
      callback(settings || this.getDefaultSettings());
    });
  }

  // Unsubscribe from settings changes
  unsubscribeFromSettings(listenerId) {
    tenantDataService.unsubscribe(listenerId);
  }

  // Legacy localStorage methods for backward compatibility
  static getSettings() {
    const settings = localStorage.getItem('clinicSettings');
    return settings ? JSON.parse(settings) : new SettingsService().getDefaultSettings();
  }

  static saveSettings(settings) {
    localStorage.setItem('clinicSettings', JSON.stringify(settings));
  }

  static updateSettings(updatedSettings) {
    const currentSettings = this.getSettings();
    const newSettings = { ...currentSettings, ...updatedSettings };
    this.saveSettings(newSettings);
    return newSettings;
  }

  // Specific setting getters
  async getTreatmentTypes() {
    const settings = await this.getSettings();
    return settings.treatmentTypes || [];
  }

  async getRoleRates() {
    const settings = await this.getSettings();
    return settings.roleRates || { admin: 70, senior_clinician: 75, clinician: 65, junior_clinician: 55, staff: 45 };
  }

  async getVatPercentage() {
    const settings = await this.getSettings();
    return settings.vatPercentage || 20;
  }

  async getBankDetails() {
    const settings = await this.getSettings();
    return settings.bankDetails || { accountName: '', accountNumber: '', sortCode: '' };
  }

  async getBusinessHours() {
    const settings = await this.getSettings();
    return settings.businessHours || this.getDefaultSettings().businessHours;
  }

  // Treatment type helpers
  async addTreatmentType(treatmentType) {
    const settings = await this.getSettings();
    const treatmentTypes = [...(settings.treatmentTypes || []), treatmentType];
    return await this.updateSettings({ ...settings, treatmentTypes });
  }

  async updateTreatmentType(index, treatmentType) {
    const settings = await this.getSettings();
    const treatmentTypes = [...(settings.treatmentTypes || [])];
    treatmentTypes[index] = treatmentType;
    return await this.updateSettings({ ...settings, treatmentTypes });
  }

  async deleteTreatmentType(index) {
    const settings = await this.getSettings();
    const treatmentTypes = [...(settings.treatmentTypes || [])];
    treatmentTypes.splice(index, 1);
    return await this.updateSettings({ ...settings, treatmentTypes });
  }
}

const settingsService = new SettingsService();
export { settingsService };
export default settingsService;
